import React, { useState, useEffect } from 'react';
import { 
  Badge, 
  Avatar, 
  Tooltip, 
  Tag, 
  Space, 
  Typography, 
  Popover,
  Button
} from 'antd';
import { 
  EditOutlined, 
  UserOutlined, 
  ClockCircleOutlined,
  EyeOutlined,
  FileTextOutlined,
  HistoryOutlined
} from '@ant-design/icons';
import { 
  subscribeToEditSessions, 
  getDocumentCollaborationStatus 
} from '../services/documentEditService';

const { Text } = Typography;

/**
 * DocumentCollaborationStatus - Shows real-time collaboration indicators
 * 
 * Displays who's editing, recent activity, and document status
 * without requiring expensive Office 365 licensing.
 */
const DocumentCollaborationStatus = ({ 
  documentId, 
  compact = false, 
  showVersionInfo = true,
  onEditClick = null 
}) => {
  const [collaborationStatus, setCollaborationStatus] = useState({
    isBeingEdited: false,
    activeEditors: [],
    hasRecentActivity: false,
    recentVersions: [],
    lastModified: null,
    totalActiveSessions: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!documentId) return;

    let unsubscribe = () => {};

    const initializeStatus = async () => {
      try {
        setLoading(true);
        
        // Get initial collaboration status
        const initialStatus = await getDocumentCollaborationStatus(documentId);
        setCollaborationStatus(initialStatus);
        
        // Subscribe to real-time updates
        unsubscribe = subscribeToEditSessions(documentId, (activeSessions) => {
          setCollaborationStatus(prev => ({
            ...prev,
            isBeingEdited: activeSessions.length > 0,
            activeEditors: activeSessions.map(session => ({
              userId: session.userId,
              userName: session.userName,
              userEmail: session.userEmail,
              startedAt: session.startedAt?.toDate(),
              lastActivity: session.lastActivityAt?.toDate()
            })),
            totalActiveSessions: activeSessions.length
          }));
        });
        
      } catch (error) {
        console.error('❌ Error initializing collaboration status:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeStatus();

    return () => {
      unsubscribe();
    };
  }, [documentId]);

  const formatTimeAgo = (date) => {
    if (!date) return 'Unknown';
    
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getStatusColor = () => {
    if (collaborationStatus.isBeingEdited) return '#52c41a'; // Green - active editing
    if (collaborationStatus.hasRecentActivity) return '#1890ff'; // Blue - recent activity
    return '#8c8c8c'; // Gray - no recent activity
  };

  const getStatusText = () => {
    if (collaborationStatus.isBeingEdited) {
      const editorCount = collaborationStatus.totalActiveSessions;
      return editorCount === 1 ? 'Being edited' : `${editorCount} editors`;
    }
    if (collaborationStatus.hasRecentActivity) return 'Recently updated';
    return 'Available';
  };

  const renderActiveEditors = () => {
    if (collaborationStatus.activeEditors.length === 0) return null;

    const content = (
      <div style={{ maxWidth: '300px' }}>
        <div style={{ marginBottom: '12px', fontWeight: 'bold' }}>
          <EditOutlined /> Currently Editing:
        </div>
        <Space direction="vertical" style={{ width: '100%' }}>
          {collaborationStatus.activeEditors.map((editor, index) => (
            <div key={editor.userId || index} style={{ 
              display: 'flex', 
              alignItems: 'center', 
              padding: '8px',
              backgroundColor: '#f5f5f5',
              borderRadius: '6px'
            }}>
              <Avatar 
                size="small" 
                icon={<UserOutlined />} 
                style={{ marginRight: '8px' }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 'bold', fontSize: '13px' }}>
                  {editor.userName}
                </div>
                <div style={{ fontSize: '11px', color: '#666' }}>
                  Started {formatTimeAgo(editor.startedAt)}
                </div>
              </div>
            </div>
          ))}
        </Space>
        <div style={{ marginTop: '12px', fontSize: '11px', color: '#666' }}>
          <ClockCircleOutlined /> Desktop editing in Office applications
        </div>
      </div>
    );

    return (
      <Popover content={content} trigger="hover" placement="bottom">
        <Space size="small">
          {collaborationStatus.activeEditors.slice(0, 3).map((editor, index) => (
            <Tooltip key={editor.userId || index} title={`${editor.userName} (editing)`}>
              <Avatar 
                size="small" 
                icon={<UserOutlined />}
                style={{ 
                  backgroundColor: '#52c41a',
                  border: '2px solid white',
                  marginLeft: index > 0 ? '-8px' : '0'
                }}
              />
            </Tooltip>
          ))}
          {collaborationStatus.activeEditors.length > 3 && (
            <Avatar 
              size="small" 
              style={{ 
                backgroundColor: '#1890ff',
                marginLeft: '-8px'
              }}
            >
              +{collaborationStatus.activeEditors.length - 3}
            </Avatar>
          )}
        </Space>
      </Popover>
    );
  };

  const renderRecentActivity = () => {
    if (!showVersionInfo || !collaborationStatus.hasRecentActivity) return null;

    const recentVersion = collaborationStatus.recentVersions[0];
    if (!recentVersion) return null;

    return (
      <Tooltip title={`Updated ${formatTimeAgo(recentVersion.uploadedAt?.toDate())} by ${recentVersion.uploadedBy}`}>
        <Tag 
          icon={<HistoryOutlined />} 
          color="blue" 
          size="small"
          style={{ margin: '0 4px' }}
        >
          v{recentVersion.version}
        </Tag>
      </Tooltip>
    );
  };

  const renderCompactView = () => (
    <Space size="small" align="center">
      <Badge 
        status="processing" 
        color={getStatusColor()}
        style={{ marginRight: '4px' }}
      />
      
      {collaborationStatus.isBeingEdited && renderActiveEditors()}
      
      {!collaborationStatus.isBeingEdited && (
        <Text style={{ fontSize: '12px', color: '#666' }}>
          {getStatusText()}
        </Text>
      )}
      
      {renderRecentActivity()}
    </Space>
  );

  const renderFullView = () => (
    <div style={{ padding: '8px 0' }}>
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        {/* Status Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Space size="small">
            <Badge 
              status="processing" 
              color={getStatusColor()}
            />
            <Text strong style={{ fontSize: '14px' }}>
              {getStatusText()}
            </Text>
          </Space>
          
          {onEditClick && (
            <Button 
              type="primary" 
              size="small" 
              icon={<EditOutlined />}
              onClick={onEditClick}
              disabled={collaborationStatus.isBeingEdited}
            >
              Edit in Office
            </Button>
          )}
        </div>

        {/* Active Editors */}
        {collaborationStatus.isBeingEdited && (
          <div style={{ marginLeft: '20px' }}>
            {renderActiveEditors()}
          </div>
        )}

        {/* Recent Activity */}
        {showVersionInfo && collaborationStatus.lastModified && (
          <div style={{ marginLeft: '20px' }}>
            <Space size="small">
              <ClockCircleOutlined style={{ color: '#666' }} />
              <Text style={{ fontSize: '12px', color: '#666' }}>
                Last modified {formatTimeAgo(collaborationStatus.lastModified)}
              </Text>
              {renderRecentActivity()}
            </Space>
          </div>
        )}

        {/* Edit Instructions for Available Documents */}
        {!collaborationStatus.isBeingEdited && onEditClick && (
          <div style={{ 
            marginLeft: '20px', 
            padding: '8px 12px', 
            backgroundColor: '#f0f8ff', 
            borderRadius: '6px',
            border: '1px solid #e6f4ff'
          }}>
            <Space size="small">
              <FileTextOutlined style={{ color: '#1890ff' }} />
              <Text style={{ fontSize: '11px', color: '#1890ff' }}>
                Click "Edit in Office" to download and edit in your desktop application
              </Text>
            </Space>
          </div>
        )}
      </Space>
    </div>
  );

  if (loading) {
    return (
      <Space size="small">
        <Badge status="default" />
        <Text style={{ fontSize: '12px', color: '#ccc' }}>Loading...</Text>
      </Space>
    );
  }

  return compact ? renderCompactView() : renderFullView();
};

export default DocumentCollaborationStatus; 